import type { QueryDocumentsParams } from '@/api/firebase';
import { FirebaseApi } from '@/api/firebase';
import { DEFAULT_QUERY_LIMIT, DOCUMENTS_PAGE_SIZE } from '@shared/constants';
import type { OrderBy } from '@shared/types';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { firebaseKeys } from './firebase.keys';

function sortKey(sortState: OrderBy | null): string {
  if (!sortState?.field) return '';
  const field = sortState.field === '__id__' ? '__name__' : sortState.field;
  return `${field}:${sortState.direction}`;
}

export function useCollections(connectionId: string | null) {
  return useQuery({
    queryKey: connectionId
      ? firebaseKeys.collections(connectionId)
      : ['firebase', 'collections', 'disabled'],
    queryFn: () => FirebaseApi.getCollections(connectionId!),
    enabled: !!connectionId,
  });
}

export function useDocument(
  connectionId: string | null,
  collectionPath: string | null,
  documentId: string | null
) {
  return useQuery({
    queryKey:
      connectionId && collectionPath && documentId
        ? firebaseKeys.document(connectionId, collectionPath, documentId)
        : ['firebase', 'document', 'disabled'],
    queryFn: () => FirebaseApi.getDocument(connectionId!, collectionPath!, documentId!),
    enabled: !!connectionId && !!collectionPath && !!documentId,
  });
}

export function useSubcollections(
  connectionId: string | null,
  documentPath: string,
  enabled: boolean = true
) {
  return useQuery({
    queryKey:
      connectionId && documentPath
        ? firebaseKeys.subcollections(connectionId, documentPath)
        : ['firebase', 'subcollections', 'disabled'],
    queryFn: () => FirebaseApi.getSubcollections(connectionId!, documentPath),
    enabled: !!enabled && !!connectionId && !!documentPath,
  });
}

/** Query by document ID prefix via POST /query (same API as query builder). */
export function useSearchDocuments(
  connectionId: string | null,
  collectionPath: string | null,
  q: string,
  limit: number = DEFAULT_QUERY_LIMIT
) {
  const trimmed = q.trim();
  return useQuery({
    queryKey:
      connectionId && collectionPath && trimmed
        ? firebaseKeys.search(connectionId, collectionPath, trimmed, limit)
        : ['firebase', 'search', 'disabled'],
    queryFn: async () => {
      const response = await FirebaseApi.queryDocuments(connectionId!, collectionPath!, {
        connectionId: connectionId!,
        documentIdPrefix: trimmed,
        limit,
      });
      if (!response.validation.valid && response.validation.errors.length > 0) {
        return [];
      }
      return response.results?.documents ?? [];
    },
    enabled: !!connectionId && !!collectionPath && !!trimmed,
  });
}

export function useDocumentsInfinite(
  connectionId: string | null,
  collectionPath: string | null,
  sortState: OrderBy | null,
  options?: { enabled?: boolean }
) {
  const key = sortKey(sortState);
  const enabled =
    (options?.enabled !== false) && !!connectionId && !!collectionPath;
  return useInfiniteQuery({
    queryKey:
      connectionId && collectionPath
        ? firebaseKeys.documentsInfinite(connectionId, collectionPath, key)
        : ['firebase', 'documentsInfinite', 'disabled'],
    queryFn: async ({ pageParam }) => {
      const orderBy = sortState?.field
        ? [
            {
              field: sortState.field === '__id__' ? '__name__' : sortState.field,
              direction: sortState.direction,
            },
          ]
        : undefined;
      const body: QueryDocumentsParams = {
        connectionId: connectionId!,
        filters: undefined,
        orderBy,
        limit: DOCUMENTS_PAGE_SIZE,
        ...(pageParam ? { startAfter: pageParam } : {}),
      };
      const response = await FirebaseApi.queryDocuments(connectionId!, collectionPath!, body);
      if (!response.validation.valid && response.validation.errors.length > 0) {
        const errorMessages = response.validation.errors
          .map((e) => `${e.path}: ${e.message}`)
          .join('\n');
        throw new Error(`Query validation failed:\n${errorMessages}`);
      }
      const documents = response.results?.documents ?? [];
      const lastDocument = documents.length > 0 ? documents[documents.length - 1] : undefined;
      // Backend may set hasMore; otherwise treat full page as "potentially more"
      const hasMore =
        response.results?.hasMore === true || documents.length === DOCUMENTS_PAGE_SIZE;
      return {
        documents,
        lastDocument,
        hasMore,
        limit: DOCUMENTS_PAGE_SIZE,
      };
    },
    getNextPageParam: (lastPage) => {
      const pageSize = lastPage.limit;
      const hasNext =
        lastPage.hasMore === true || lastPage.documents.length === pageSize;
      const next = hasNext ? lastPage.lastDocument : undefined;
      return next;
    },
    initialPageParam: undefined as unknown,
    enabled,
  });
}
